import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  ChevronRight,
  Loader2,
  X,
  AlertTriangle,
  Pause,
  Play,
  Zap,
  Clock,
  Calendar
} from 'lucide-react';
import { useSubscription } from '../context/SubscriptionContext';
import { useConfig } from '../context/ConfigContext';
import { useToast } from '../context/ToastContext';
import useModalBack from '../hooks/useModalBack';
import { isIOS } from '../services/native';

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { formatDate } = useConfig();
  const { toast } = useToast();
  const {
    subscription, plans, loading, isPremium, isTrialing, isActive,
    isPaused, isCancelled, isExpired, trialDaysLeft, subscriptionDaysLeft,
    willCancel, cancelSubscription, pauseSubscription, resumeSubscription,
    getInvoices, refreshSubscription, hidePaymentUI, userCurrency
  } = useSubscription();

  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  useModalBack(!!selectedPayment, () => setSelectedPayment(null), 'PaymentDetail');
  useModalBack(showCancelModal, () => { setShowCancelModal(false); setCancelReason(''); }, 'CancelSubscription');

  useEffect(() => { fetchInvoices(); }, []);

  const fetchInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const data = await getInvoices({ limit: 10 });
      setInvoices(data.payments || []);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await cancelSubscription(cancelReason);
      toast.success('Subscription cancelled. Access continues until period end.');
      setShowCancelModal(false);
      setCancelReason('');
    } catch (err) {
      toast.error(err.message || 'Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePause = async () => {
    setActionLoading(true);
    try {
      await pauseSubscription();
      toast.success('Subscription paused');
    } catch (err) {
      toast.error(err.message || 'Failed to pause subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    setActionLoading(true);
    try {
      await resumeSubscription();
      toast.success('Subscription resumed');
    } catch (err) {
      toast.error(err.message || 'Failed to resume subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusInfo = () => {
    if (isTrialing) return { emoji: '⏳', label: 'Trial', color: '#92400e', bg: '#fffbeb', border: '#fde68a' };
    if (isActive) return { emoji: '✅', label: 'Active', color: '#166534', bg: '#f0fdf4', border: '#bbf7d0' };
    if (isPaused) return { emoji: '⏸️', label: 'Paused', color: '#1e40af', bg: '#eff6ff', border: '#bfdbfe' };
    if (isCancelled || willCancel) return { emoji: '❌', label: willCancel ? 'Cancelling' : 'Cancelled', color: '#991b1b', bg: '#fef2f2', border: '#fecaca' };
    if (isExpired) return { emoji: '⏰', label: 'Expired', color: '#475569', bg: '#f8fafc', border: '#e2e8f0' };
    return { emoji: '📋', label: 'Free', color: '#475569', bg: '#f8fafc', border: '#e2e8f0' };
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  const status = getStatusInfo();
  const trialText = trialDaysLeft === 0 ? 'Trial ends today' : trialDaysLeft === 1 ? '1 day left' : `${trialDaysLeft} days left`;

  return (
    <div className="pb-24 xl:pb-8 space-y-3">
      {/* Header */}
      <div className="xl:hidden">
        <h1 className="text-lg font-semibold text-surface-900">Subscription</h1>
        <p className="text-xs text-surface-500">Manage your plan</p>
      </div>
      <div className="hidden xl:block page-header">
        <h1 className="page-title">Subscription</h1>
        <p className="page-subtitle">Manage your plan</p>
      </div>

      {/* Upgrade prompt */}
      {!hidePaymentUI && (!isActive || isTrialing) && (
        <button
          onClick={() => navigate('/pricing')}
          className="w-full flex items-center justify-between p-3 rounded-2xl transition-all active:scale-[0.99]"
          style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}
        >
          <div className="flex items-center gap-2.5">
            <span className="text-lg">⚡</span>
            <span className="text-[13px] font-semibold" style={{ color: '#166534' }}>View Plans & Subscribe</span>
          </div>
          <ChevronRight className="w-4 h-4" style={{ color: '#16a34a' }} />
        </button>
      )}

      {/* Current Plan Card */}
      <div className="bg-white rounded-2xl border border-surface-200 p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#ede9fe' }}>
            <span className="text-xl">👑</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-bold text-surface-900">{plans?.planName || 'Premium'}</p>
            <p className="text-[11px] text-surface-400">
              {isTrialing ? 'Trial' : hidePaymentUI ? 'Premium' : (plans?.intervals?.[subscription?.billingInterval || 'monthly']?.[userCurrency || 'INR']?.formatted || plans?.pricing?.[userCurrency || 'INR']?.formatted || '-') + '/' + ({ monthly: 'month', quarterly: '3 months', yearly: 'year' }[subscription?.billingInterval] || 'month')}
            </p>
          </div>
          <span
            className="text-[11px] font-semibold px-2.5 py-1 rounded-lg flex items-center gap-1"
            style={{ background: status.bg, color: status.color, border: `1px solid ${status.border}` }}
          >
            {status.label}
          </span>
        </div>

        {/* Status details */}
        {isTrialing && (
          <div className="flex items-center gap-2.5 py-3" style={{ borderBottom: '1px solid #f1f5f9' }}>
            <Clock className="w-4 h-4" style={{ color: '#b45309' }} />
            <div>
              <p className="text-[13px] font-semibold text-surface-900">{trialText}</p>
              <p className="text-[11px] text-surface-400">Trial ends {formatDate(subscription?.trialEndDate)}</p>
            </div>
          </div>
        )}

        {isActive && !willCancel && (
          <div className="flex items-center gap-2.5 py-3" style={{ borderBottom: '1px solid #f1f5f9' }}>
            <Calendar className="w-4 h-4" style={{ color: '#16a34a' }} />
            <div>
              <p className="text-[13px] font-semibold text-surface-900">Next billing: {formatDate(subscription?.currentPeriodEnd)}</p>
              <p className="text-[11px] text-surface-400">Auto-renews {subscription?.billingInterval || 'monthly'}</p>
            </div>
          </div>
        )}

        {willCancel && (
          <div className="flex items-center gap-2.5 py-3" style={{ borderBottom: '1px solid #f1f5f9' }}>
            <AlertTriangle className="w-4 h-4" style={{ color: '#ef4444' }} />
            <div>
              <p className="text-[13px] font-semibold text-surface-900">Cancellation scheduled</p>
              <p className="text-[11px] text-surface-400">Access until {formatDate(subscription?.willCancelAt || subscription?.currentPeriodEnd)}</p>
            </div>
          </div>
        )}

        {isPaused && (
          <div className="flex items-center gap-2.5 py-3" style={{ borderBottom: '1px solid #f1f5f9' }}>
            <Pause className="w-4 h-4" style={{ color: '#2563eb' }} />
            <div>
              <p className="text-[13px] font-semibold text-surface-900">Subscription paused</p>
              <p className="text-[11px] text-surface-400">Paused on {formatDate(subscription?.pausedAt)}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        {isTrialing && !hidePaymentUI && (
          <button
            onClick={() => navigate('/pricing')}
            className="w-full mt-3 py-2.5 rounded-xl font-semibold text-[13px] flex items-center justify-center gap-2 transition-all"
            style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
          >
            <Zap className="w-4 h-4" />Subscribe Now
          </button>
        )}

        {isActive && !willCancel && subscription?.paymentProvider !== 'apple' && (
          <div className="mt-3 space-y-2">
            <button
              onClick={handlePause}
              disabled={actionLoading}
              className="w-full py-2.5 rounded-xl bg-surface-100 text-surface-700 font-semibold text-[13px] flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pause className="w-4 h-4" />}
              Pause Subscription
            </button>
            <button
              onClick={() => setShowCancelModal(true)}
              className="w-full py-2.5 rounded-xl text-[13px] font-semibold flex items-center justify-center gap-2 transition-colors"
              style={{ color: '#ef4444' }}
            >
              <X className="w-4 h-4" />Cancel Subscription
            </button>
          </div>
        )}

        {/* Apple: Manage subscription via Apple Settings */}
        {isActive && subscription?.paymentProvider === 'apple' && (
          <button
            onClick={() => window.open('https://apps.apple.com/account/subscriptions', '_blank')}
            className="w-full mt-3 py-2.5 rounded-xl bg-surface-100 text-surface-700 font-semibold text-[13px] flex items-center justify-center gap-2 transition-colors"
          >
            Manage in Apple Settings
          </button>
        )}

        {isPaused && subscription?.paymentProvider !== 'apple' && (
          <button
            onClick={handleResume}
            disabled={actionLoading}
            className="w-full mt-3 py-2.5 rounded-xl font-semibold text-[13px] flex items-center justify-center gap-2 transition-all disabled:opacity-50"
            style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
          >
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Resume Subscription
          </button>
        )}

        {(isExpired || (isCancelled && !isPremium)) && !hidePaymentUI && (
          <button
            onClick={() => navigate('/pricing')}
            className="w-full mt-3 py-2.5 rounded-xl font-semibold text-[13px] flex items-center justify-center gap-2 transition-all"
            style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
          >
            <Zap className="w-4 h-4" />Resubscribe
          </button>
        )}
      </div>

      {/* Payment History — hide for Apple subscribers (Apple handles billing) */}
      {!hidePaymentUI && subscription?.paymentProvider !== 'apple' && (
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <div className="flex items-center gap-3 pb-3.5" style={{ borderBottom: '1px solid #f1f5f9' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#fef9c3' }}>
              <span className="text-lg">🧾</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-bold text-surface-900">Payment History</p>
              <p className="text-[11px] text-surface-400">{invoices.length > 0 ? `${invoices.length} payment${invoices.length > 1 ? 's' : ''}` : 'No payments yet'}</p>
            </div>
          </div>

          {invoicesLoading ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-surface-400" />
            </div>
          ) : invoices.length > 0 ? (
            <div>
              {invoices.map((invoice, idx) => (
                <button
                  key={invoice._id}
                  onClick={() => setSelectedPayment(invoice)}
                  className="w-full flex items-center justify-between py-3 text-left active:bg-surface-50 transition-colors"
                  style={idx < invoices.length - 1 ? { borderBottom: '1px solid #f1f5f9' } : {}}
                >
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{
                        background: invoice.status === 'success' ? '#dcfce7' : invoice.status === 'failed' ? '#fef2f2' : '#f1f5f9'
                      }}
                    >
                      {invoice.status === 'success' ? (
                        <Check className="w-4 h-4" style={{ color: '#16a34a' }} />
                      ) : invoice.status === 'failed' ? (
                        <X className="w-4 h-4" style={{ color: '#ef4444' }} />
                      ) : (
                        <Clock className="w-4 h-4" style={{ color: '#94a3b8' }} />
                      )}
                    </div>
                    <div>
                      <p className="text-[13px] font-semibold text-surface-900">
                        {invoice.formattedAmount || `${invoice.currency === 'USD' ? '$' : '₹'}${(invoice.amount / 100).toFixed(2)}`}
                      </p>
                      <p className="text-[10px] text-surface-400">{formatDate(invoice.createdAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{
                        background: invoice.status === 'success' ? '#dcfce7' : invoice.status === 'failed' ? '#fef2f2' : '#f1f5f9',
                        color: invoice.status === 'success' ? '#16a34a' : invoice.status === 'failed' ? '#ef4444' : '#94a3b8'
                      }}
                    >
                      {invoice.status === 'success' ? 'Paid' : invoice.status === 'failed' ? 'Failed' : 'Pending'}
                    </span>
                    <ChevronRight className="w-3.5 h-3.5 text-surface-300" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <span className="text-3xl block mb-2">💳</span>
              <p className="text-[13px] text-surface-400">No payment history yet</p>
            </div>
          )}
        </div>
      )}

      {/* Referral — hidden on iOS (Apple doesn't allow external referral programs) */}
      {!hidePaymentUI && !isIOS && (
        <button
          onClick={() => navigate('/referrals')}
          className="w-full flex items-center justify-between p-3 rounded-2xl bg-white border border-surface-200 transition-all active:bg-surface-50"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#ede9fe' }}>
              <span className="text-sm">🎁</span>
            </div>
            <div className="text-left">
              <p className="text-[13px] font-semibold text-surface-900">Refer & Earn</p>
              <p className="text-[11px] text-surface-400">Invite friends, earn discounts</p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-surface-400" />
        </button>
      )}

      {/* Payment Detail Modal */}
      {selectedPayment && (
        <div
          className="fixed inset-0 z-[110] bg-surface-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
          onClick={() => setSelectedPayment(null)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-1 sm:hidden">
              <div className="w-10 h-1 rounded-full bg-surface-300" />
            </div>
            <div className="p-4">
              {/* Header */}
              <div className="flex items-center gap-3 pb-3.5" style={{ borderBottom: '1px solid #f1f5f9' }}>
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: selectedPayment.status === 'success' ? '#dcfce7' : selectedPayment.status === 'failed' ? '#fef2f2' : '#f1f5f9'
                  }}
                >
                  {selectedPayment.status === 'success' ? (
                    <Check className="w-5 h-5" style={{ color: '#16a34a' }} />
                  ) : selectedPayment.status === 'failed' ? (
                    <X className="w-5 h-5" style={{ color: '#ef4444' }} />
                  ) : (
                    <Clock className="w-5 h-5" style={{ color: '#94a3b8' }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-bold text-surface-900">Payment Details</p>
                  <p className="text-[11px] text-surface-400">
                    {selectedPayment.status === 'success' ? 'Payment successful' : selectedPayment.status === 'failed' ? 'Payment failed' : 'Payment pending'}
                  </p>
                </div>
                <button onClick={() => setSelectedPayment(null)} className="p-1.5 rounded-lg hover:bg-surface-100 transition-colors">
                  <X className="w-4 h-4 text-surface-400" />
                </button>
              </div>

              {/* Amount */}
              <div className="py-4 text-center" style={{ borderBottom: '1px solid #f1f5f9' }}>
                <p className="text-3xl font-bold text-surface-900" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {selectedPayment.formattedAmount || `${selectedPayment.currency === 'USD' ? '$' : '₹'}${(selectedPayment.amount / 100).toFixed(2)}`}
                </p>
                {selectedPayment.discountAmount > 0 && (
                  <p className="text-[11px] text-surface-400 mt-1">
                    <span className="line-through">{selectedPayment.currency === 'USD' ? '$' : '₹'}{((selectedPayment.originalAmount || selectedPayment.amount + selectedPayment.discountAmount) / 100).toFixed(2)}</span>
                    <span className="ml-1.5 text-green-600 font-semibold">saved {selectedPayment.currency === 'USD' ? '$' : '₹'}{(selectedPayment.discountAmount / 100).toFixed(2)}</span>
                  </p>
                )}
              </div>

              {/* Details rows */}
              <div className="py-2">
                {[
                  { label: 'Date', value: formatDate(selectedPayment.createdAt) },
                  { label: 'Status', value: selectedPayment.status === 'success' ? 'Paid' : selectedPayment.status === 'failed' ? 'Failed' : 'Pending' },
                  { label: 'Type', value: selectedPayment.paymentType === 'subscription' ? 'Subscription' : selectedPayment.paymentType === 'renewal' ? 'Renewal' : selectedPayment.paymentType || '-' },
                  { label: 'Method', value: selectedPayment.method ? `${selectedPayment.method}${selectedPayment.methodDetails ? ` (${selectedPayment.methodDetails})` : ''}` : '-' },
                  ...(selectedPayment.couponApplied ? [{ label: 'Coupon', value: selectedPayment.couponApplied }] : []),
                  ...(selectedPayment.razorpayPaymentId ? [{ label: 'Payment ID', value: selectedPayment.razorpayPaymentId }] : []),
                  ...(selectedPayment.failureReason ? [{ label: 'Failure Reason', value: selectedPayment.failureReason }] : []),
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5" style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <span className="text-[12px] text-surface-400">{row.label}</span>
                    <span className="text-[12px] font-semibold text-surface-700 text-right max-w-[60%] break-all">{row.value}</span>
                  </div>
                ))}
              </div>

              {selectedPayment.invoiceUrl && selectedPayment.status === 'success' && (
                <a
                  href={selectedPayment.invoiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 mt-2 rounded-xl text-sm font-semibold text-white"
                  style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  View Invoice
                </a>
              )}

            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div
          className="fixed inset-0 z-[110] bg-surface-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
          onClick={() => { setShowCancelModal(false); setCancelReason(''); }}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-1 sm:hidden">
              <div className="w-10 h-1 rounded-full bg-surface-300" />
            </div>
            <div className="p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#fef2f2' }}>
                  <AlertTriangle className="w-5 h-5" style={{ color: '#ef4444' }} />
                </div>
                <div>
                  <h3 className="text-[15px] font-bold text-surface-900">Cancel Subscription?</h3>
                  <p className="text-[11px] text-surface-400">Access continues until period end</p>
                </div>
              </div>

              <p className="text-[12px] text-surface-500 mb-3">
                Your subscription will remain active until the end of the current billing period. You can resubscribe anytime.
              </p>

              <label className="block text-[12px] font-semibold text-surface-500 mb-1.5">
                Reason (optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Help us improve..."
                rows={3}
                className="w-full px-3 py-2.5 rounded-xl border border-surface-200 bg-surface-50 focus:outline-none focus:ring-2 focus:ring-expense-500 resize-none text-[13px] text-surface-900 placeholder-surface-400"
              />
            </div>

            <div className="p-4 pt-0 flex gap-2">
              <button
                onClick={() => { setShowCancelModal(false); setCancelReason(''); }}
                className="flex-1 py-2.5 rounded-xl bg-surface-100 text-surface-700 font-semibold text-[13px] transition-colors"
              >
                Keep Plan
              </button>
              <button
                onClick={handleCancel}
                disabled={actionLoading}
                className="flex-1 py-2.5 rounded-xl font-semibold text-[13px] text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: '#ef4444' }}
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
